  /**
 * They Trust Us — custom reviews carousel (3 per view desktop, 1 mobile).
 * Slide: translateX. Fade: opacity crossfade then jump transform (no slideshow-component).
 */

const MQ_DESKTOP = '(min-width: 750px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class TheyTrustUsReviewsCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #pagination = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  #page = 0;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MediaQueryList} */
  #mqDesktop = window.matchMedia(MQ_DESKTOP);
  /** @type {() => void} */
  #onResize = () => this.#layout();
  /** @type {() => void} */
  #onMqChange = () => {
    this.#page = 0;
    this.#layout();
  };

  connectedCallback() {
    const root = this.closest('.they-trust-us__reviews-carousel-shell');
    this.#viewport = this.querySelector('[data-trust-carousel-viewport]');
    this.#track = this.querySelector('[data-trust-carousel-track]');
    this.#slides = this.#track ? Array.from(this.#track.querySelectorAll('[data-trust-carousel-slide]')) : [];
    this.#pagination = this.querySelector('[data-trust-carousel-pagination]');
    this.#prev = root?.querySelector('.they-trust-us__reviews-nav--start [data-action="previous"]') ?? null;
    this.#next = root?.querySelector('.they-trust-us__reviews-nav--end [data-action="next"]') ?? null;

    this.#prev?.addEventListener('click', this.#handlePrev);
    this.#next?.addEventListener('click', this.#handleNext);

    this.#mqDesktop.addEventListener('change', this.#onMqChange);

    if (this.#viewport) {
      this.#resizeObserver = new ResizeObserver(this.#onResize);
      this.#resizeObserver.observe(this.#viewport);
    }

    this.#layout();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#handlePrev);
    this.#next?.removeEventListener('click', this.#handleNext);
    this.#mqDesktop.removeEventListener('change', this.#onMqChange);
    this.#resizeObserver?.disconnect();
  }

  get #slidesPerView() {
    return this.#mqDesktop.matches ? 3 : 1;
  }

  get #slideCount() {
    return this.#slides.length;
  }

  get #totalPages() {
    const n = this.#slideCount;
    if (n === 0) return 1;
    return Math.max(1, Math.ceil(n / this.#slidesPerView));
  }

  /** Pixel gap as applied on the track (must match flex `gap` or transform drifts). */
  #getGapPx() {
    const track = this.#track;
    if (track) {
      const g = getComputedStyle(track).gap || getComputedStyle(track).columnGap;
      const token = String(g ?? '')
        .trim()
        .split(/\s+/)[0];
      const parsed = parseFloat(token || '0');
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    }
    const raw = getComputedStyle(this).getPropertyValue('--trust-reviews-slide-gap').trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 20;
  }

  #handlePrev = () => this.#go(-1);
  #handleNext = () => this.#go(1);

  /**
   * @param {number} page
   */
  #goTo(page) {
    if (this.#totalPages <= 1) return;
    const next = Math.max(0, Math.min(this.#totalPages - 1, page));
    if (next === this.#page) return;

    const fade = this.dataset.transition === 'fade';
    const reduce = window.matchMedia(REDUCED_MOTION).matches;

    this.#page = next;

    if (fade && !reduce) {
      this.#fadeThenApply();
    } else {
      this.#applyTransform(true);
      this.#syncAria();
      this.#syncDots();
    }
  }

  /**
   * @param {number} delta - +1 or -1
   */
  #go(delta) {
    if (this.#totalPages <= 1) return;

    let next = this.#page + delta;
    if (next < 0) next = this.#totalPages - 1;
    else if (next >= this.#totalPages) next = 0;

    if (next === this.#page) return;

    const fade = this.dataset.transition === 'fade';
    const reduce = window.matchMedia(REDUCED_MOTION).matches;

    this.#page = next;

    if (fade && !reduce) {
      this.#fadeThenApply();
    } else {
      this.#applyTransform(true);
      this.#syncAria();
      this.#syncDots();
    }
  }

  #fadeThenApply() {
    const track = this.#track;
    if (!track) return;

    track.style.transition = 'opacity 0.28s ease';
    track.style.opacity = '0';

    window.setTimeout(() => {
      this.#applyTransform(false);
      this.#syncAria();
      this.#syncDots();
      track.style.opacity = '0';
      void track.offsetHeight;
      track.style.transition = 'opacity 0.32s ease';
      track.style.opacity = '1';
    }, 280);
  }

  /**
   * @param {boolean} animate
   */
  #applyTransform(animate) {
    const viewport = this.#viewport;
    const track = this.#track;
    if (!viewport || !track) return;

    const vw = viewport.clientWidth;
    const spv = this.#slidesPerView;
    const gap = this.#getGapPx();
    const totalGap = (spv - 1) * gap;
    // Floor + 1px buffer: flex can round flex-basis up so a 4th slide peeks past the clip edge.
    let slideW = Math.floor(((vw - totalGap - 1) / spv) * 100) / 100;
    if (slideW < 0) slideW = 0;

    for (const slide of this.#slides) {
      slide.style.flex = `0 0 ${slideW}px`;
      slide.style.minWidth = `${slideW}px`;
      slide.style.maxWidth = `${slideW}px`;
    }

    const startIndex = this.#page * spv;
    const offset = startIndex * (slideW + gap);

    const reduce = window.matchMedia(REDUCED_MOTION).matches;
    if (reduce || !animate) {
      track.style.transition = 'none';
    } else {
      track.style.transition = 'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1)';
    }
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  #layout() {
    this.#page = Math.min(this.#page, Math.max(0, this.#totalPages - 1));
    if (this.dataset.transition === 'fade' && !window.matchMedia(REDUCED_MOTION).matches) {
      const track = this.#track;
      if (track) {
        track.style.opacity = '1';
        track.style.transition = '';
      }
    }
    this.#applyTransform(false);
    this.#syncAria();
    this.#syncDots(true);
  }

  #syncAria() {
    const spv = this.#slidesPerView;
    const start = this.#page * spv;
    this.#slides.forEach((slide, i) => {
      const visible = i >= start && i < start + spv;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  /**
   * Builds/updates dots UI.
   * @param {boolean} [forceBuild]
   */
  #syncDots(forceBuild = false) {
    const host = this.#pagination;
    if (!host) return;

    // Dots are intended for mobile only; CSS hides them on desktop, but keep DOM in sync anyway.
    const total = this.#totalPages;
    const active = this.#page;

    const existing = host.querySelectorAll('button[data-trust-carousel-dot]');
    if (forceBuild || existing.length !== total) {
      host.innerHTML = '';

      if (total <= 1) {
        host.setAttribute('hidden', '');
        return;
      }

      host.removeAttribute('hidden');

      const ol = document.createElement('ol');
      ol.className = 'they-trust-us__reviews-dots';

      for (let i = 0; i < total; i += 1) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'they-trust-us__reviews-dot';
        btn.dataset.trustCarouselDot = String(i);
        btn.setAttribute('aria-label', `Go to slide ${i + 1} of ${total}`);
        btn.addEventListener('click', () => this.#goTo(i));
        li.appendChild(btn);
        ol.appendChild(li);
      }

      host.appendChild(ol);
    } else {
      host.toggleAttribute('hidden', total <= 1);
    }

    const dots = host.querySelectorAll('button[data-trust-carousel-dot]');
    dots.forEach((btn, i) => {
      if (i === active) btn.setAttribute('aria-selected', 'true');
      else btn.removeAttribute('aria-selected');
    });
  }
}

if (!customElements.get('they-trust-us-reviews-carousel')) {
  customElements.define('they-trust-us-reviews-carousel', TheyTrustUsReviewsCarousel);
}
